import { GroupCondition } from '@/api/enums';
import { equal, group, nested } from '@/api/helpers';
import { FilterNamesEnum } from '@/client/helpers';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { useAccountUsers } from '@/hooks/query/users.hooks';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { FlexContainer } from '@/ui/styled-ui';
import { NeverLoggedInUsersIcon } from '@/utils/helpers';
import { Skeleton } from 'primereact/skeleton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const NeverLoggedInUsersContainer = styled(FlexContainer)`
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  flex: 1 0 50%;
  margin-top: var(--small-padding);
  background-color: rgba(63, 157, 223, 0.1);
  padding: var(--default-padding);
  border-radius: var(--xsmall-border-radius);

  &:hover {
    cursor: pointer;
  }

  .text-container {
    margin-left: var(--small-padding);
    p {
      color: var(--blue-darkest);
      margin: 0;
    }

    .header-text {
      font-size: var(--semi-big-font-size);
      line-height: var(--semi-big-line-height);
      font-weight: 700;
    }

    .description-text {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      font-weight: 400;
    }
  }

  .number-container {
    font-size: var(--heading-large-font-size);
    line-height: var(--heading-large-line-height);
    font-weight: 700;
    color: var(--blue-darkest);
  }
`;

const LogoContainer = styled(FlexContainer)`
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  min-width: 38px;
  min-height: 38px;
  background-color: rgba(63, 157, 223, 0.7);
  border-radius: var(--xsmall-border-radius);
`;

export const NeverLoggedInUsers = () => {
  const account = useAppSelector(selectCurrentAccount);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { users: neverLoggedInUsers, isLoading: areNeverLoggedInUsersLoading } =
    useAccountUsers({
      accountId: account?.id,
      take: 0,
      skip: 0,
      filters: [
        group(GroupCondition.AND, [
          equal('active', true),
          nested('meta', [equal('lastLogin', null)]),
        ]),
      ],
    });

  const onNeverLoggedInClick = () => {
    const urlParams = new URLSearchParams({
      filters: JSON.stringify({
        [FilterNamesEnum.NEVER_LOGGED_IN]: 'yes',
      }),
    });
    navigate(
      `${RedirectPaths[
        RedirectPathsEnum.REPORTS_SUMMARY
      ]()}?${urlParams.toString()}`,
      { state: { isDashboardLink: true } },
    );
  };

  return (
    <NeverLoggedInUsersContainer
      id="never-logged-in-users"
      onClick={onNeverLoggedInClick}
    >
      <LogoContainer>
        <NeverLoggedInUsersIcon />
      </LogoContainer>
      <FlexContainer
        className="text-container"
        direction="column"
        align="flex-start"
        justify="center"
      >
        <p className="header-text">
          {t('dashboard.awareness.never.logged.in.title')}
        </p>
        <p className="description-text">
          {t('dashboard.awareness.never.logged.in.description')}
        </p>
      </FlexContainer>
      {areNeverLoggedInUsersLoading ? (
        <div className="number-container">
          <Skeleton width="100%" height="100%" />
        </div>
      ) : (
        <div className="number-container">{neverLoggedInUsers?.count}</div>
      )}
    </NeverLoggedInUsersContainer>
  );
};
